/**
 * Find Us Section - Store Locator with Interactive Map
 * Handles Leaflet map initialization, markers, and store interactions
 */

class FindUsSection {
  constructor(container) {
    this.container = container;
    this.mapElement = container.querySelector('[id^="find-us-map-"]');
    this.storeList = container.querySelector('[id^="store-list-"]');
    this.storeItems = container.querySelectorAll('.find-us__store-item');

    if (!this.mapElement) {
      return;
    }

    this.sectionId = this.mapElement.dataset.sectionId;
    this.stores = this.parseStoresData();

    this.map = null;
    this.markers = [];

    this.init();
  }

  parseCoordinate(value) {
    const normalized = String(value == null ? '' : value).trim().replace(',', '.');
    const coordinate = parseFloat(normalized);
    return Number.isNaN(coordinate) ? null : coordinate;
  }

  parseStoresData() {
    try {
      const scriptElement = document.getElementById(`stores-data-${this.sectionId}`);

      if (scriptElement) {
        return JSON.parse(scriptElement.textContent);
      }

      const storesData = this.mapElement.dataset.stores;
      return JSON.parse(storesData);
    } catch (error) {
      console.error('FindUs: Error parsing stores data:', error.message);
      return [];
    }
  }

  init() {
    if (typeof L === 'undefined') {
      return;
    }

    this.initMap();
    this.initStoreListeners();
    this.initMobileToggle();
  }

  initMap() {
    this.map = L.map(this.mapElement.id, {
      center: [46.6, 2.5],
      zoom: 6,
      scrollWheelZoom: false,
      dragging: true,
      touchZoom: true,
      doubleClickZoom: true,
      boxZoom: true,
      keyboard: true,
      zoomControl: true
    });

    // Fond OpenStreetMap France : labels 100% en français (Londres, Bruxelles,
    // Madrid, Rome…), contrairement à CARTO qui restait en anglais. Style clair.
    L.tileLayer('https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> France',
      subdomains: 'abc',
      maxZoom: 20
    }).addTo(this.map);

    this.addMarkers();

    // Vue initiale cadrée sur la France métropolitaine (Corse incluse).
    // On NE fait PAS un fitBounds sur tous les marqueurs : des points lointains
    // (La Réunion, Belgique…) dézoomeraient la carte à l'échelle du globe.
    // Les marqueurs hors cadre restent présents et accessibles en dézoomant.
    const FRANCE_METRO_BOUNDS = L.latLngBounds(
      [41.3, -5.2],  // sud-ouest
      [51.1, 9.6]    // nord-est
    );
    this.map.fitBounds(FRANCE_METRO_BOUNDS);

    setTimeout(() => {
      this.map.invalidateSize();
    }, 100);
  }

  addMarkers() {
    const markerIcon = L.divIcon({
      className: 'custom-marker-icon',
      html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="24" height="24">
        <path fill-rule="evenodd" d="m9.69 18.933.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 0 0 .281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 1 0 3 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 0 0 2.273 1.765 11.842 11.842 0 0 0 .976.544l.062.029.018.008.006.003ZM10 11.25a2.25 2.25 0 1 0 0-4.5 2.25 2.25 0 0 0 0 4.5Z" clip-rule="evenodd" />
      </svg>`,
      iconSize: [24, 24],
      iconAnchor: [12, 24],
      popupAnchor: [0, -24]
    });

    this.stores.forEach((store, index) => {
      const lat = this.parseCoordinate(store.lat);
      const lng = this.parseCoordinate(store.lng);

      if (lat === null || lng === null) {
        return;
      }

      const marker = L.marker([lat, lng], {
        icon: markerIcon,
        title: store.name
      }).addTo(this.map);

      marker.on('click', () => {
        this.highlightStore(index);
        this.scrollToStore(index);
      });

      this.markers[index] = marker;
    });
  }

  initStoreListeners() {
    this.storeItems.forEach((item, index) => {
      item.addEventListener('click', (e) => {
        if (e.target.tagName === 'A' || e.target.tagName === 'BUTTON' || e.target.closest('button')) {
          return;
        }

        this.highlightStore(index);
        this.zoomToStore(index);
      });
    });
  }

  initMobileToggle() {
    const toggleButtons = this.container.querySelectorAll('.find-us__toggle-btn');

    toggleButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.stopPropagation();

        const parent = button.closest('.find-us__store-item');
        const contentType = button.dataset.toggle;
        const addressContent = parent.querySelector('[data-content="address"]');
        const hoursContent = parent.querySelector('[data-content="hours"]');
        const allButtons = parent.querySelectorAll('.find-us__toggle-btn');

        allButtons.forEach(btn => {
          btn.classList.remove('active');
          btn.setAttribute('aria-pressed', 'false');
        });

        button.classList.add('active');
        button.setAttribute('aria-pressed', 'true');

        if (window.innerWidth < 990) {
          if (contentType === 'address') {
            if (addressContent) addressContent.style.display = 'flex';
            if (hoursContent) hoursContent.style.display = 'none';
          } else {
            if (addressContent) addressContent.style.display = 'none';
            if (hoursContent) hoursContent.style.display = 'flex';
          }
        }
      });
    });

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (window.innerWidth >= 990) {
          this.container.querySelectorAll('[data-content]').forEach(content => {
            content.style.display = 'flex';
          });
        } else {
          this.container.querySelectorAll('.find-us__store-item').forEach(item => {
            const addressContent = item.querySelector('[data-content="address"]');
            const hoursContent = item.querySelector('[data-content="hours"]');
            const addressBtn = item.querySelector('[data-toggle="address"]');
            const hoursBtn = item.querySelector('[data-toggle="hours"]');

            if (addressContent && hoursContent) {
              addressContent.style.display = 'flex';
              hoursContent.style.display = 'none';
              addressBtn?.classList.add('active');
              hoursBtn?.classList.remove('active');
            }
          });
        }
      }, 250);
    });

    if (window.innerWidth < 990) {
      this.container.querySelectorAll('.find-us__store-item').forEach(item => {
        const hoursContent = item.querySelector('[data-content="hours"]');
        if (hoursContent) {
          hoursContent.style.display = 'none';
        }
      });
    }
  }

  highlightStore(index) {
    this.storeItems.forEach(item => {
      item.style.backgroundColor = '';
    });

    if (this.storeItems[index]) {
      this.storeItems[index].style.backgroundColor = 'rgba(var(--color-foreground), 0.04)';

      setTimeout(() => {
        this.storeItems[index].style.backgroundColor = '';
      }, 2000);
    }
  }

  scrollToStore(index) {
    if (this.storeItems[index]) {
      const headerOffset = 100;
      const elementPosition = this.storeItems[index].getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }

  zoomToStore(index) {
    if (this.markers[index]) {
      this.map.setView(
        this.markers[index].getLatLng(),
        13,
        { animate: true, duration: 0.5 }
      );
    }
  }
}

// Initialize all Find Us sections
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('.find-us-section');
  sections.forEach(section => {
    new FindUsSection(section);
  });
});

// Handle Shopify theme editor events
if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.find-us-section');
    if (section) {
      new FindUsSection(section);
    }
  });

  document.addEventListener('shopify:section:reorder', () => {
    setTimeout(() => {
      const sections = document.querySelectorAll('.find-us-section');
      sections.forEach(section => {
        new FindUsSection(section);
      });
    }, 100);
  });
}
